import { useMutation } from '@apollo/client'
import { Button, FormControl, Heading, Input, Modal, VisuallyHidden } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import { useGA4Events } from '../../../hooks'
import { COMPLETE_SIGNUP_NUDGE_ORIGIN } from '../constants'
import {
  CompleteSignupNudgePhoneMutation,
  CompleteSignupNudgePhoneMutationVariables,
} from './__generated__/mutations.graphql'
import illustration from './assets/phone-step-illustration.svg'
import styles from './complete-signup-modal.module.scss'
import {
  NUDGE_CLICK_PROPS,
  NUDGE_INPUT_START_PROPS,
  NUDGE_INPUT_SUBMIT_PROPS,
  NUDGE_RENDER_PROPS,
} from './ga4-events'
import { COMPLETE_SIGNUP_NUDGE_PHONE_MUTATION } from './mutations'

const TITLE = 'Quer receber avisos sobre seus processos no WhatsApp?'
const PHONE_VALID_LENGTHS = [10, 11]

function isPhoneValid(digits: string): boolean {
  return PHONE_VALID_LENGTHS.includes(digits.length)
}

interface PhoneStepProps {
  onActivated: () => void
  onSkip: () => void
}

export function PhoneStep({ onActivated, onSkip }: PhoneStepProps) {
  const ga4 = useGA4Events()
  const formRef = useRef<HTMLFormElement>(null)
  const inputStartedRef = useRef(false)
  const {
    ref: phoneRef,
    unmaskedValue,
    value: phoneValue,
  } = useIMask(useRef({ mask: '(00) 00000-0000', lazy: false, placeholderChar: '_' }).current)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [phoneError, setPhoneError] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const [editPhone, { loading }] = useMutation<
    CompleteSignupNudgePhoneMutation,
    CompleteSignupNudgePhoneMutationVariables
  >(COMPLETE_SIGNUP_NUDGE_PHONE_MUTATION)

  useEffect(() => {
    ga4.sendComponentRender(NUDGE_RENDER_PROPS.PHONE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleInputStart() {
    if (inputStartedRef.current) return
    inputStartedRef.current = true
    ga4.sendInputStart(NUDGE_INPUT_START_PROPS.PHONE)
  }

  function handlePhoneBlur() {
    setPhoneTouched(true)
    const digits = (unmaskedValue || '').replace(/\D/g, '')
    if (digits.length > 0) setPhoneError(!isPhoneValid(digits))
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setPhoneTouched(true)
    const digits = (unmaskedValue || '').replace(/\D/g, '')
    if (!isPhoneValid(digits)) {
      setPhoneError(true)
      ga4.sendInputSubmitComponent(NUDGE_INPUT_SUBMIT_PROPS.PHONE_FALSE)
      return
    }
    setPhoneError(false)
    setSubmitError(undefined)
    try {
      await editPhone({
        variables: {
          input: {
            area: digits.substring(0, 2),
            phone: digits.substring(2),
            action: 'ADD',
            origin: COMPLETE_SIGNUP_NUDGE_ORIGIN,
            sendNumberRegistrationWhatsappMessage: true,
          },
        },
      })
      ga4.sendInputSubmitComponent(NUDGE_INPUT_SUBMIT_PROPS.PHONE_TRUE)
      onActivated()
    } catch {
      setSubmitError('Não foi possível cadastrar o número. Tente novamente.')
      ga4.sendInputSubmitComponent(NUDGE_INPUT_SUBMIT_PROPS.PHONE_FALSE)
    }
  }

  function onSkipClick() {
    ga4.sendClick(NUDGE_CLICK_PROPS.PHONE_SKIP)
    onSkip()
  }

  const hasError = (phoneTouched && phoneError) || !!submitError
  const errorMessage = submitError || 'Informe um número de telefone válido'

  return (
    <>
      <Modal.Body data-testid="complete-signup-phone-step">
        <div className={`${styles.illustratedHeader} ${styles.phoneSpacing}`}>
          <Image src={illustration} alt="" width={296} height={175} unoptimized />
          <VisuallyHidden>
            <Modal.HeaderTitle>{TITLE}</Modal.HeaderTitle>
          </VisuallyHidden>
          <Heading size="lg">{TITLE}</Heading>
          <Modal.HeaderDescription>
            <p>Seja avisado sobre audiências, sentenças e novos processos envolvendo seu CPF.</p>
          </Modal.HeaderDescription>
        </div>
        <form ref={formRef} className={styles.form} onSubmit={onSubmit} noValidate>
          <FormControl error={hasError}>
            <FormControl.Label>Telefone</FormControl.Label>
            <Input
              name="phone"
              type="tel"
              inputMode="tel"
              ref={phoneRef}
              value={phoneValue || ''}
              disabled={loading}
              onInput={handleInputStart}
              onBlur={handlePhoneBlur}
            />
            <FormControl.ErrorMessage>{hasError && errorMessage}</FormControl.ErrorMessage>
          </FormControl>
        </form>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <Button kind="primary" loading={loading} onClick={() => formRef.current?.requestSubmit()}>
          Sim, ativar avisos
        </Button>
        <Button kind="plain" disabled={loading} onClick={onSkipClick}>
          Agora não
        </Button>
      </Modal.Footer>
    </>
  )
}
